import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  Model,
  ValidatedResponse,
  PatchWithMeta,
  PagedResponse,
  QueryParams,
  ChangeMeta,
} from "@ofqual/portal-core/core/types";
import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";

import { InformationRequest, fromApiModel } from "../domain/InformationRequest";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { Recipient } from "../domain/Recipient";
import { RecipientUpdate } from "../domain/RecipientUpdate";
import { InformationRequestAction } from "../domain/InformationRequestAction";
import { AssignUser } from "../domain/AssignUser";
import { Message } from "../domain/Message";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";

export class InformationRequestService extends BaseRemoteService {
  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<InformationRequest>> {
    const url = `${this.apiRoot}/informationrequests/${paramsToSearchString(
      params
    )}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);
    return PagedResponseTo<InformationRequest>(response, fromApiModel);
  }

  public async create(opts: Opts): Promise<string> {
    const url = `${this.apiRoot}/informationrequests`;
    const { id } = await super.postRequest<{ id: string }>(url, null, opts);
    return id;
  }

  public async get(id: string, opts: Opts): Promise<InformationRequest> {
    const url = `${this.apiRoot}/informationrequests/${id}`;
    const response = await super.getRequest<Model>(url, opts);
    return fromApiModel(response);
  }

  public async update(
    id: string,
    patchDocument: PatchWithMeta<InformationRequest>,
    opts: Opts
  ): Promise<ValidatedResponse<InformationRequest>> {
    const url = `${this.apiRoot}/informationrequests/${id}`;
    try {
      const response = await this.patchRequest<
        InformationRequest,
        PatchWithMeta<InformationRequest>
      >(url, patchDocument, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async send(
    id: string,
    opts: Opts
  ): Promise<ValidatedResponse<InformationRequest>> {
    const url = `${this.apiRoot}/informationrequests/${id}/send`;
    try {
      const response: Model = await this.postRequest(url, null, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async getRecipients(id: string, opts: Opts): Promise<Recipient[]> {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/`;
    return await super.getRequest<Recipient[]>(url, opts);
  }

  public async getRecipientsAo(
    id: string,
    itemId: string,
    opts: Opts
  ): Promise<Recipient[]> {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${itemId}`;
    return await super.getRequest<Recipient[]>(url, opts);
  }

  public async postRecipients(
    id: string,
    recipients: RecipientUpdate[],
    patch: ChangeMeta[],
    opts: Opts
  ) {
    try {
      const url = `${this.apiRoot}/informationrequests/${id}/recipients`;
      return await super.postRequest<Recipient[]>(url, { recipients:recipients, meta:patch }, opts);
    } catch (err) {
      throw err;
    }
  }

  public async assignUsers(id: string, model: AssignUser[], opts: Opts) {
    try {
      const url = `${this.apiRoot}/informationrequests/${id}/assign`;
      return await super.postRequest<Recipient[]>(url, model, opts);
    } catch (err) {
      throw err;
    }
  }

  public async getActions(
    id: string,
    opts: Opts
  ): Promise<InformationRequestAction[]> {
    const url = `${this.apiRoot}/informationrequests/${id}/actions`;
    return await super.getRequest<InformationRequestAction[]>(url, opts);
  }

  public async getActionsAo(
    id: string,
    itemId: string,
    opts: Opts
  ): Promise<InformationRequestAction[]> {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${itemId}/actions`;
    return await super.getRequest<InformationRequestAction[]>(url, opts);
  }

  public async postAction(
    id: string,
    files: File[],
    action: Partial<InformationRequestAction>,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/actions/`;
    const actionSerialised = JSON.stringify(action);
    return await super.postRequestFormData(
      url,
      { files, actionSerialised },
      opts
    );
  }

  public async updateAction(
    id: string,
    actionId: string,
    files: File[],
    model: PatchWithMeta<InformationRequestAction>,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/actions/${actionId}`;
    const patchSerialised = JSON.stringify(model.document);

    return await super.postRequestFormData(
      url,
      { files, patchSerialised },
      opts
    );
  }

  public async deleteAttachment(
    id: string,
    actionId: string,
    attachmentId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/actions/${actionId}/attachments/${attachmentId}`;
    return await super.deleteRequest(url, opts);
  }

  public async deleteAction(
    id: string,
    actionId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/actions/${actionId}`;
    return await super.deleteRequest(url, opts);
  }


  public async deleteCao(
    id: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}`;
    return await super.deleteRequest(url, opts);
  }


  public async downloadAttachment(
    id: string,
    actionId: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/actions/${actionId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async sendMessageToRecipients(
    id: string,
    files: File[],
    message: Message,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/messages`;
    const messageSerialised = JSON.stringify(message);
    return await super.postRequestFormData(
      url,
      { files, messageSerialised },
      opts
    );
  }

  public async downloadAllDocuments(
    id: string,
    orgRecipientId: string,
    actionId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/actions/${actionId}/download`;
    return await super.getRequestBlob(url, opts);
  }

  public async postDocumentAction(
    id: string,
    orgRecipientId: string,
    actionId: string,
    files: File[],
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/actions/${actionId}/documents`;
    return await super.postRequestFormData(url, { files }, opts);
  }

  public async postMessageAction(
    id: string,
    orgRecipientId: string,
    actionId: string,
    message: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/actions/${actionId}/message`;
    return await super.postRequest(url, message, opts);
  }

  public async downloadActivityDocument(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async sendMessage(
    id: string,
    orgRecipientId: string,
    files: File[],
    message: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/messages`;
    return await super.postRequestFormData(url, { files, message }, opts);
  }

  public async updateAoUsers(
    id: string,
    orgRecipientId: string,
    users: { userId: string; upn: string }[],
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/users`;
    return await super.postRequest(url, users, opts);
  }

  public async assignAoUser(
    id: string,
    orgRecipientId: string,
    aoAssignedToUpn: string,
    aoAssignedToName: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/assign`;
    return await super.postRequest(url, { assignedToUpn: aoAssignedToUpn, assignedToDisplayName: aoAssignedToName }, opts);
  }

  public async closeRecipients(id: string, orgIds: string[], opts: Opts) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/close`
    return await super.postRequest(url, orgIds, opts);
  }

  public async readDocuments(
    id: string,
    orgRecipientId: string,
    actionId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/actions/${actionId}/read`;
    return await super.postRequest(url, null, opts);
  }

  public async markResponseComplete(
    id: string,
    orgRecipientId: string,
    actionId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/actions/${actionId}/markResponseComplete`;
    return await super.postRequest(url, null, opts);
  }

  public async markUploadComplete(
    id: string,
    orgRecipientId: string,
    actionId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/informationrequests/${id}/recipients/${orgRecipientId}/actions/${actionId}/markUploadComplete`;
    return await super.postRequest(url, null, opts);
  }


}
